import { inject } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { FilterDef } from '@common/data-models/navigation/filter-def';
import { FilterTypes } from '@common/enums/filter-types';
import { ViewStateSortOders } from '@common/enums/viewstate-sort-orders';
import { GeneralViewStateTypes, ViewStateTypes, ViewStateTypesEnum } from '@common/enums/viewstate-types';
import { Store } from '@ngrx/store';
import { ViewStateOptions } from '@src/suite/common/data-models/navigation/viewstate-options';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { ViewStateRecordsAlias } from '@src/suite/common/services/layout-state';
import { CreateViewStateActions } from '@src/suite/common/store/createviewstate/createviewstate.actions';
import {
    selectCreateViewInEditMode,
    selectCreateViewIsCopy,
    selectCreateViewIsLoading,
    selectCreateViewStateAllFilters,
    selectCreateViewStateFilter,
    selectCreateViewStateFilters,
    selectCreateViewStateGeneralType,
    selectCreateViewStateGroupBy,
    selectCreateViewStateGroupBySortIcon,
    selectCreateViewStateHasOptionalFilters,
    selectCreateViewStateHasRequiredFilters,
    selectCreateViewStateLayout,
    selectCreateViewStateOptionalFilters,
    selectCreateViewStateOptionalFiltersIDs,
    selectCreateViewStateRequiredFilters,
    selectCreateViewStateRequiredFiltersIDs,
    selectCreateViewStateSortBy,
    selectCreateViewStateTitle,
    selectCreateViewStateType,
    selectCurrentSortBySortIcon,
} from '@src/suite/common/store/createviewstate/createviewstate.selectors';

export class BaseViewState {
    protected store = inject(Store);
    protected globalTablesService = inject(GlobalTablesService);

    public generalViewStateTypes = GeneralViewStateTypes;
    public viewStateTypes = ViewStateTypesEnum;

    public viewStateSortOrders = ViewStateSortOders;

    public viewStateRecord: ViewStateRecordsAlias;

    isLoading$ = this.store.select(selectCreateViewIsLoading);
    inEditMode$ = this.store.select(selectCreateViewInEditMode);
    isCopy$ = this.store.select(selectCreateViewIsCopy);

    viewStateGeneralType$ = this.store.select(selectCreateViewStateGeneralType);
    viewStateType$ = this.store.select(selectCreateViewStateType);
    viewStateTitle$ = this.store.select(selectCreateViewStateTitle);

    viewStateFilter$ = this.store.select(selectCreateViewStateFilter);
    viewStateFilters$ = this.store.select(selectCreateViewStateFilters);
    viewStateAllFilters$ = this.store.select(selectCreateViewStateAllFilters);
    viewStateRequiredFilters$ = this.store.select(selectCreateViewStateRequiredFilters);
    viewStateRequiredFiltersIDs$ = this.store.select(selectCreateViewStateRequiredFiltersIDs);
    viewStateHasRequiredFilters$ = this.store.select(selectCreateViewStateHasRequiredFilters);
    viewStateOptionalFilters$ = this.store.select(selectCreateViewStateOptionalFilters);
    viewStateOptionalFiltersIDs$ = this.store.select(selectCreateViewStateOptionalFiltersIDs);
    viewStateHasOptionalFilters$ = this.store.select(selectCreateViewStateHasOptionalFilters);

    viewStateLayout$ = this.store.select(selectCreateViewStateLayout);

    viewStateGrouping$ = this.store.select(selectCreateViewStateGroupBy);
    viewStateGroupByIcon$ = this.store.select(selectCreateViewStateGroupBySortIcon);
    viewStateSorting$ = this.store.select(selectCreateViewStateSortBy);
    viewStateSortByIcon$ = this.store.select(selectCurrentSortBySortIcon);

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    updateTitle(value: string) {
        this.store.dispatch(CreateViewStateActions.updateViewStateTitle({ title: value }));
    }

    updateGeneralViewStateType(generalType: number) {
        if (generalType === GeneralViewStateTypes.SINGLEFEED) this.store.dispatch(CreateViewStateActions.updateViewStateType({ vstype: ViewStateTypes.RSSFEED }));
        if (generalType === GeneralViewStateTypes.MULTIPLEFEED) this.store.dispatch(CreateViewStateActions.updateViewStateType({ vstype: ViewStateTypes.MULTIPLEFEED }));
    }

    updateViewStateType(vsType: number) {
        this.store.dispatch(CreateViewStateActions.updateViewStateType({ vstype: vsType }));
    }

    updateGroupBy(options: ViewStateOptions, groupBy: string) {
        options.Value = groupBy;
        this.store.dispatch(CreateViewStateActions.updateViewStateGroupBy({ groupBy: groupBy }));
    }

    updateSortBy(options: ViewStateOptions, sortBy: string) {
        options.Value = sortBy;
        this.store.dispatch(CreateViewStateActions.updateViewStateSortBy({ sortBy: sortBy }));
    }

    updateLayout(options: ViewStateOptions, layout: string) {
        options.Value = layout;
        this.store.dispatch(CreateViewStateActions.updateViewStateLayout({ layout: layout }));
    }

    public toggleGroupSortOrder() {
        this.store.dispatch(CreateViewStateActions.toggleGroupSortOder());
    }

    public toggleSortOrder() {
        this.store.dispatch(CreateViewStateActions.toggleSortOder());
    }

    public onClearAllFilters(required: boolean) {
        this.store.dispatch(CreateViewStateActions.removeAllViewStateFilters({ required: required }));
    }

    onFilterStateChange(event: MatSelectionListChange, fromRequired: boolean) {
        const selected = event.source.selectedOptions.selected.map(x => x.value);
        this.store.dispatch(CreateViewStateActions.addViewStateFilters({ required: fromRequired, filters: selected }));
    }

    onUpdateFilter(required: boolean) {
        this.store.dispatch(CreateViewStateActions.updateViewStateFilter({ required }));
    }

    onRemoveFilter(item: FilterDef, type: FilterTypes) {
        this.store.dispatch(CreateViewStateActions.removeViewStateFilter({ required: type === FilterTypes.REQUIRED, filter: item.ID }));
    }
}
